<template>
  <nav class="sb-stepper">
    <ol
      class="sb-stepper-list"
      :class="{
        'sb-stepper-list--vertical': isVertical,
        'sb-stepper-list--smaller': isSmaller,
      }"
    >
      <SbStep
        v-for="(step, index) in steps"
        :key="step._uid"
        :step="step"
        :step-index="index"
        :is-active="isCurrentStep(index)"
        :is-completed="isCompletedStep(index)"
        :is-vertical="isVertical"
        :is-smaller="isSmaller"
        @step-click="navigateToStep"
        @step-key-press="handleStepKeyPress"
      >
        <template #step>
          <slot />
        </template>
      </SbStep>
    </ol>
  </nav>
</template>

<script lang="ts" setup>
import { ref, watch } from 'vue'
import type { Step } from './types'
import SbStep from './components/Step/SbStep.vue'

export type SbStepperProps = {
  steps?: Step[]
  stepIndex?: number
  isVertical?: boolean
  isSmaller?: boolean
}

const props = withDefaults(defineProps<SbStepperProps>(), {
  steps: () => [],
  stepIndex: 0,
  isVertical: false,
  isSmaller: false,
})

const emit = defineEmits<{
  (e: 'step-index-change', index: number): void
}>()

const currentStepIndex = ref(0)

watch(
  () => props.stepIndex,
  (index: number) => {
    currentStepIndex.value = index
  },
  { immediate: true },
)

watch(currentStepIndex, (index: number) => {
  emit('step-index-change', index)
})

function handleStepKeyPress(event: KeyboardEvent, step: Step) {
  switch (event.code) {
    case 'ArrowRight':
      navigateToNextStep(event)
      break
    case 'ArrowLeft':
      navigateToPreviousStep(event)
      break
    case 'Enter':
    case 'NumpadEnter':
    case 'Space':
      navigateToStep(step)
      break
    case 'Tab':
    default:
      break
  }
}

function navigateToNextStep(event: KeyboardEvent) {
  const stepElement = event.target
  if (!stepElement || !(stepElement instanceof HTMLElement)) {
    return
  }
  const nextStepIndex = +stepElement.getAttribute('data-step-id')! + 1
  const nextStepElement = stepElement.nextElementSibling

  if (
    nextStepElement &&
    nextStepElement instanceof HTMLElement &&
    (isCompletedStep(nextStepIndex) || isCurrentStep(nextStepIndex))
  ) {
    setFocusOnStep(stepElement, nextStepElement)
  }
}

function navigateToPreviousStep(event: KeyboardEvent) {
  const stepElement = event.target

  if (!stepElement || !(stepElement instanceof HTMLElement)) {
    return
  }

  const previousStepIndex = +stepElement.getAttribute('data-step-id')! - 1
  const previousStepTarget = stepElement.previousElementSibling

  if (
    previousStepTarget &&
    previousStepTarget instanceof HTMLElement &&
    (isCompletedStep(previousStepIndex) || isCurrentStep(previousStepIndex))
  ) {
    setFocusOnStep(stepElement, previousStepTarget)
  }
}

function setFocusOnStep(
  currentTarget: HTMLElement,
  focusableTarget: HTMLElement,
) {
  currentTarget.tabIndex = -1
  focusableTarget.tabIndex = 0
  focusableTarget.focus()
}

function navigateToStep(step: Step): void {
  const stepIndex = getStepIndex(step)

  if (stepIndex < currentStepIndex.value) {
    currentStepIndex.value = stepIndex
  }
}

function getStepIndex(step: Step): number {
  return props.steps.findIndex((s: Step) => s._uid === step._uid)
}

function isCurrentStep(stepIndex: number): boolean {
  const step = props.steps[stepIndex]
  return step ? getStepIndex(step) === currentStepIndex.value : false
}

function isCompletedStep(stepIndex: number): boolean {
  const step = props.steps[stepIndex]
  if (!step) {
    return false
  }

  return step.completed || getStepIndex(step) < currentStepIndex.value
}
</script>

<style lang="scss">
@import 'stepper';
</style>
